import {
  Box,
  Button,
  Card,
  Cell,
  IconButton,
  Image,
  Layout,
  Page,
  Pagination,
  Search,
  Table,
  TableToolbar,
  TextButton,
  Tooltip,
  WixDesignSystemProvider,
  Text,
} from "@wix/design-system";
import React, { useEffect, useState } from "react";
import LinkedProduct from "../../../models/linked-product";
import { withProviders } from "../../withProviders";
import _ from "lodash";
import searchLogo from "./../search.svg";
import "../index.css";
import * as Icons from "@wix/wix-ui-icons-common";
import "@wix/design-system/styles.global.css";
import WixLogo from "../wix_logo.svg";
import FiareLogo from "../fiare_logo.svg";
import Loading from "../../../components/common/Loader";
import Picture from "../../images/no-image.png";
import { truncateText } from "../../../services/utils/productUtils";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";

const Index = () => {
  /*
    |--------------------------------------------------------------------------
    | Pagination details
    |--------------------------------------------------------------------------
    |
    */

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalNumberOfPages, setTotalNumberOfPages] = useState(1);

  /*
    |--------------------------------------------------------------------------
    | Linked product
    |--------------------------------------------------------------------------
    |
    */

  const [linkedProducts, setLinkedProducts] = useState<any>([]);

  /*
    |--------------------------------------------------------------------------
    | 
    |--------------------------------------------------------------------------
    |
    */
  const [isLoading, setIsLoading] = useState(false);

  /*
    |--------------------------------------------------------------------------
    | Filter parameters
    |--------------------------------------------------------------------------
    |
    */

  const [keywords, setKeywords] = useState("");

  /*
    |--------------------------------------------------------------------------
    | Get filter parameters
    |--------------------------------------------------------------------------
    |
    */

  const getFilterParameters = () => {
    return {
      keywords: keywords,
    };
  };

  /*
    |--------------------------------------------------------------------------
    | Handle page change
    |--------------------------------------------------------------------------
    |
    */

  const handlePageChange = ({ page }: { page: number }) => {
    setCurrentPageNumber(page);
  };

  /*
    |--------------------------------------------------------------------------
    | Fetch linked products
    |--------------------------------------------------------------------------
    |
    */

  const fetchLinkedProducts = () => {
    let filterParameters = getFilterParameters();

    /*
      |--------------------------------------------------------------------------
      | Append pagination query params
      |--------------------------------------------------------------------------
      |
      */

    filterParameters.page = currentPageNumber;

    setIsLoading(true);

    LinkedProduct.get(filterParameters)
      .then((response) => {
        let newLinkedProducts: any[] = [];

        response?.data?.data.map((product: any) => {
          newLinkedProducts.push({
            ...product,
            expandable: true,
            expanded: false,
          });
        });

        setLinkedProducts(newLinkedProducts);
        setIsLoading(false);
        /*
          |--------------------------------------------------------------------------
          | Set pagination details
          |--------------------------------------------------------------------------
          |
          */
        const total = response?.data?.last_page;
        setTotalNumberOfPages(total);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  /*
    |--------------------------------------------------------------------------
    | Columns
    |--------------------------------------------------------------------------
    |
    */

  const columns = [
    {
      title: "",
      width: "8%",
      render: (row: any) => (
        <Layout>
          <Cell span={3}>
            <Box width="80px" height="80px" overflow="hidden">
              {row.thumbnail ? <img src={row.thumbnail} /> : <img src={Picture} />}
            </Box>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Products",
      width: "20%",
      render: (row: any) => (
        <>
          <Tooltip content={row.product_name}>
            <Text
              style={{
                fontSize: "14px ",
                lineHeight: "18px",
              }}
              weight="bold"
            >
              {truncateText(row.product_name, 30)}
            </Text>
          </Tooltip>
        </>
      ),
    },
    {
      title: "SKU",
      width: "18%",
      render: (row: any) => row.sku,
    },
    {
      title: "Inventory",
      width: "10%",
      render: (row: any) => {
        const hasWixInventory = row.wix_variants.some(
          (variant: any) => variant.quantity != 0 && variant.trackQuantity == 1
        );
        const hasFaireInventory = row.faire_variants.some(
          (variant: any) => variant.available_quantity != 0 && variant.track_quantity == 1
        );

        return <Text size="small">{hasWixInventory || hasFaireInventory ? row.quantity ?? 0 : "Not Tracked"}</Text>;
      },
    },
    {
      title: "Linked Listing",
      width: "18%",
      render: (row: any) => (
        <>
          <Layout>
            <Cell span={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Tooltip appendTo="window" content="No. of products in your store">
                  <Box verticalAlign="middle" gap="SP1">
                    <img width="32px" height="12px" src={WixLogo} />
                    {row.wix_variants_count}
                  </Box>
                </Tooltip>
                <Tooltip appendTo="window" content="No. of products in your Faire store">
                  <Box verticalAlign="middle" gap="SP1">
                    <img src={FiareLogo} />
                    {row.faire_variants_count}
                  </Box>
                </Tooltip>
              </div>
            </Cell>
          </Layout>
        </>
      ),
    },
    {
      title: "Action",
      width: "10%",
      render: (row: any) => (
        <>
          <IconButton skin="standard" priority="secondary" size="tiny" onClick={() => (row.expanded = !row.expanded)}>
            {row.expanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
          </IconButton>
        </>
      ),
    },
  ];

  /*
    |--------------------------------------------------------------------------
    | Linked product details
    |--------------------------------------------------------------------------
    |
    */

  const renderLinkedProductDetails = (linkedProduct: any) => {
    const detailsColumns = [
      {
        title: "Variant",
        render: (variant: any) => <Text size="small">{variant.name}</Text>,
      },
      {
        title: "Price",
        render: (variant: any) => (
          <>
            <Text size="small">
              {variant.platform === "faire" && variant.retail_currency} &nbsp;
              {variant.formatted_price ? variant.formatted_price : variant.retail_price ?? "0.00"}
            </Text>
          </>
        ),
      },
      {
        title: "Inventory",
        render: (variant: any) => {
          const trackQuantityKey = variant.platform === "faire" ? "track_quantity" : "trackQuantity";

          return <Text size="small">{variant[trackQuantityKey] === 1 ? variant.quantity || 0 : "Not Tracked"}</Text>;
        },
      },
      {
        title: "View on",
        render: (variant: any) => (
          <>
            {variant.platform === "wix" ? (
              <Button
                size="tiny"
                skin="dark"
                showTooltip
                priority="secondary"
                as="a"
                href={variant.details_url}
                target="_blank"
              >
                <Tooltip content="View product details">Wix</Tooltip>
              </Button>
            ) : (
              <Button
                size="tiny"
                skin="dark"
                showTooltip
                priority="secondary"
                as="a"
                href={variant.faire_details_url}
                target="_blank"
              >
                <Tooltip content="View product details">Faire</Tooltip>
              </Button>
            )}
          </>
        ),
      },
    ];

    return (
      <Card showShadow hideOverflow>
        <Table
          skin="neutral"
          data={[...linkedProduct.wix_variants, ...linkedProduct.faire_variants]}
          columns={detailsColumns}
          isRowHighlight={() => true}
        >
          <Table.Content />
        </Table>
      </Card>
    );
  };

  /*
    |--------------------------------------------------------------------------
    | Render row details
    |--------------------------------------------------------------------------
    |
    */

  const renderRowDetails = (row: any) => {
    if (row.expandable) {
      return <>{renderLinkedProductDetails(row)}</>;
    }
  };

  /*
    |--------------------------------------------------------------------------
    | Render empty state
    |--------------------------------------------------------------------------
    |
    | Empty state is when there are not products to show.
    |
    */

  const renderEmptyState = () => (
    <Table.EmptyState
      title={keywords.length ? "No results found" : "No products found"}
      subtitle={
        keywords.length
          ? "No items matched your search. Try searching for something else."
          : "Couldn't find any products. Try adding new products or refine your filter."
      }
      image={<Image width="120px" height="120px" transparent={true} src={searchLogo} />}
    >
      {keywords.length > 0 && (
        <Button priority="secondary" size="small" onClick={() => setKeywords("")} prefixIcon={<Icons.Dismiss />}>
          Clear Search
        </Button>
      )}
    </Table.EmptyState>
  );

  useEffect(() => {
    fetchLinkedProducts();
  }, [currentPageNumber, keywords]);

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header
          title="Linked products"
          subtitle={
            <Text size="tiny">
              Here is a list of products linked between Wix and Faire stores based on matching SKU and the exports from
              Wix store to Faire store and vice versa.
            </Text>
          }
        />
        <Page.Content>
          <TrialExpiryAlert />

          <Card className={"linked_product_table"}>
            <Table rowDetails={(row) => renderRowDetails(row)} data={linkedProducts} columns={columns}>
              <Page.Sticky>
                <Card>
                  <TableToolbar>
                    <TableToolbar.ItemGroup position="start"></TableToolbar.ItemGroup>
                    <TableToolbar.ItemGroup position="end">
                      <TableToolbar.Item>
                        <Search
                          value={keywords}
                          placeholder="Product SKU"
                          size="small"
                          onChange={_.debounce((e: any) => {
                            setKeywords(e.target.value);
                            setCurrentPageNumber(1);
                          }, 1000)}
                          onClear={(e) => setKeywords("")}
                        />
                      </TableToolbar.Item>
                    </TableToolbar.ItemGroup>
                  </TableToolbar>
                  <Table.Titlebar />
                </Card>
              </Page.Sticky>
              {isLoading ? (
                <Loading title="Fetching Linked Products" />
              ) : linkedProducts.length ? (
                <Table.Content titleBarVisible={false} />
              ) : (
                renderEmptyState()
              )}
            </Table>
          </Card>
          <Layout justifyItems="center">
            <Cell>
              <Box margin="SP4 0 0 0">
                <Pagination
                  currentPage={currentPageNumber}
                  totalPages={totalNumberOfPages}
                  onChange={handlePageChange}
                />
              </Box>
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
